import styles from "@/../sass/components/FileUploadArea.module.scss";
import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import ValidationContext from "../Contexts/ValidationContext";
import ripple from "../Utilities/Ripple";
import axios from "axios";
import i18n from "i18next";

export function FileUploadArea(props) {
    const validation = useContext(ValidationContext);
    const name = props.name;
    const uploader = useRef({});
    const dropArea = useRef({});
    const progressBar = useRef({});
    
    const errorFromContext = props.flashMessage || (validation && validation.show && validation.getError(name));

    const [state, setState] = useState({
        uploading: false,
        percent: 0,
        latestDisabled: props.disabled || false,
        lastAttached: null,
        error: errorFromContext || '', 
    });
    
    const acceptedTypes = props.acceptedTypes || ".jpg, .jpeg, .tiff, .tif, .bmp, .png";
    const acceptedExtensions = acceptedTypes.split(',').map(ext => ext.trim().replace('.', '').toLowerCase());

    function fileChosen() {
        const files = uploader.current.files;
        if (!files || files.length === 0 || props.disabled) return;
    
        const file = files[0]; 
        setState((state) => ({ ...state, uploading: true, percent: 0, error: '' }));
    
        const fileExtension = file.name.split('.').pop().toLowerCase();
    
        if (!acceptedExtensions.includes(fileExtension)) {
            setState((state) => ({ ...state, uploading: false, error: i18n.t("organization-account.logo-error") }));
            return;
        }

        uploadFile(file);
    }
    
    function fileDropped(e) {
        e.preventDefault();
        e.stopPropagation();
    
        const files = e.dataTransfer.files;
        if (!files || files.length === 0 || state.latestDisabled) return;
    
        const file = files[0]; 
        setState((state) => ({ ...state, uploading: true, percent: 0, error: '' }));
    
        const fileExtension = file.name.split('.').pop().toLowerCase();
    
        if (!acceptedExtensions.includes(fileExtension)) {
            setState((state) => ({ ...state, uploading: false, error: i18n.t("organization-account.logo-error") }));
            return;
        }

        uploadFile(file);
        if (dropArea.current) {
            dropArea.current.classList.remove(styles.fileHover);
        }
    }    

    useEffect(() => {
        if (dropArea.current && dropArea.current !== state.lastAttached) {
            state.lastAttached = dropArea.current;

            // Attach events
            const area = dropArea.current;

            function hoverOver(e) {
                area.classList.add(styles.fileHover);
                e.preventDefault();
                e.stopPropagation();
            }

            function hoverLeave(e) {
                area.classList.remove(styles.fileHover);
                e.preventDefault();
                e.stopPropagation();
            }

            area.addEventListener('dragenter', hoverOver, false);
            area.addEventListener('dragover', hoverOver, false);
            area.addEventListener('dragleave', hoverLeave, false);
            area.addEventListener('drop', fileDropped, false);
        }
    });

    useEffect(() => {
        if (props.flashMessage) {
            setState((prevState) => ({ ...prevState, error: props.flashMessage }));
        }
    }, [props.flashMessage]);

    if (props.browseFilesRef) {
        props.browseFilesRef.open = function () {
            uploader.current.click();
        }
    }

    return (
        <div className={styles.fileUpload}>
            {state.error && 
                <p className="has-error"><br/>{state.error}</p>
            }
            {(!state.uploading && props.showUploader) && (
                <div className={`text-center ${styles.draggableFileUpload} ${styles.dragHere}`} ref={dropArea}>
                    {!props.children && (
                        <Fragment>
                            {props.dropText}&nbsp;
                            <button className="btn btn-link" type="file" onClick={(e) => {
                                e.preventDefault();
                                if (!props.disabled) uploader.current.click();
                            }} onMouseDown={ripple} onTouchStart={ripple}
                                    data-testid="file_upload-button-choose_file">{props.browseText}</button>
                            <div className={styles.infoText}>{props.instructions}</div>
                        </Fragment>
                    )}
                    {props.children}
                    <input type="file" style={{ display: "none" }} ref={uploader} onChange={fileChosen}
                           accept={acceptedTypes} />
                </div>
            )}
            {state.uploading && (
                <div className={styles.fileUploadInProgress}>
                    <div className={styles.fileProgressBar}>
                        <div className={styles.fileProgress} style={{ width: state.percent + "%" }}
                             ref={progressBar}></div>
                    </div>
                </div>
            )}
        </div>
    );

    function uploadFile(file) {
        store(file).then((data) => {
            props.onUploaded(data)
            setState(state => ({...state, uploading: false, percent: 0}))
        })
    }

    /**
     * Store a file in S3 and return its UUID, key, and other information.
     */
    async function store(file) {
        const response = await axios.post(route('mando.signed-storage-url'), {
            'content_type': file.type
        });

        let headers = response.data.headers;

        if ('Host' in headers) {
            delete headers.Host;
        }

        const cancelToken = ''

        await axios.put(response.data.url, file, {
            cancelToken: cancelToken,
            headers: headers,
            onUploadProgress: (progress) => {
                let percent = progress.loaded / progress.total;
                setState(s => ({...s, percent: Math.round(percent * 100)}));
            }
        });

        response.data.extension = file.name.split('.').pop();
        response.data.name = file.name.split('.').slice(0, -1).join('.');

        if (response.config.url.includes('.test/')) {
            response.data.url = response.data.url.slice(0, response.data.url.indexOf('?'));
        }

        return response.data;
    }
}
